import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Save, FileText, ArrowLeft } from 'lucide-react';
import { useUpdateScript } from '../hooks/useApi';
import { useCurrentUserId } from '../hooks/useCurrentUserId';
import FadeInContent from '../components/FadeInContent';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 120px);
  padding: ${props => props.theme.spacing[6]};
  background-color: ${props => props.theme.colors.background};
`;

const PageHeader = styled.div`
  width: 100%;
  max-width: 760px;
  margin-bottom: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const BackButton = styled.button`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  padding: ${props => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
  width: 100%;
  max-width: 760px;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[4]};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const Label = styled.label<{ $required?: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};

  ${(props) =>
    props.$required &&
    `
    &::after {
      content: ' *';
      color: ${props.theme.colors.error};
    }
  `}
`;

const Input = styled.input<{ $error?: boolean }>`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$error ? props.theme.colors.error : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select<{ $error?: boolean }>`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$error ? props.theme.colors.error : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  appearance: none;
  cursor: pointer;

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }

  option {
    background: ${props => props.theme.colors.backgroundSecondary};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const HelpText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textTertiary};
  margin: 0;
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing[3]};
  width: 100%;
  max-width: 760px;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${(props) => {
    switch (props.$variant) {
      case "primary":
        return `
          background: ${props.theme.colors.primary};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadows.neon};
            background: ${props.theme.colors.primaryHover};
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundSecondary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundTertiary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const NewScriptPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { userId } = useCurrentUserId();
  const updateScriptMutation = useUpdateScript();
  const [formData, setFormData] = useState({
    name: '',
    classification: 'Scripts de Conteúdo',
    intention: 'VENDA',
    type: 'conteudo' as 'conteudo' | 'mentalidade',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !userId) return;

    try {
      await updateScriptMutation.mutateAsync({
        tipo: formData.type,
        nome: formData.name,
        conteudo: '', // Conteúdo vazio inicial
        userId
      });
      navigate('/neural-vault');
    } catch (error) {
      console.error('Erro ao criar script:', error);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <FadeInContent>
      <PageContainer>
        <PageHeader>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </BackButton>
          <PageTitle>Adicionar Novo Script</PageTitle>
        </PageHeader>

        <Form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <FileText size={20} />
              <CardTitle>Detalhes do Script</CardTitle>
            </CardHeader>
            <CardBody>
              <FormRow>
                <FormGroup>
                  <Label $required>Nome do Script</Label>
                  <Input
                    type="text"
                    placeholder="Ex: preco_premium"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                  <HelpText>
                    Nome único para identificar o script (sem espaços, use underscore)
                  </HelpText>
                </FormGroup>

                <FormGroup>
                  <Label $required>Classificação</Label>
                  <Select
                    value={formData.classification}
                    onChange={(e) => handleChange('classification', e.target.value)}
                    required
                  >
                    <option value="Scripts de Conteúdo">Scripts de Conteúdo</option>
                    <option value="Mentalidades">Mentalidades</option>
                    <option value="Informações do Produto">Informações do Produto</option>
                    <option value="Configuração de Persona">Configuração de Persona</option>
                    <option value="Linguagem">Linguagem</option>
                  </Select>
                  <HelpText>
                    Categoria do script para organização
                  </HelpText>
                </FormGroup>
              </FormRow>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configuração Adicional</CardTitle>
            </CardHeader>
            <CardBody>
              <FormRow>
                <FormGroup>
                  <Label $required>Intenção</Label>
                  <Select
                    value={formData.intention}
                    onChange={(e) => handleChange('intention', e.target.value)}
                    required
                  >
                    <option value="VENDA">VENDA - Scripts de vendas e conversão</option>
                    <option value="DUVIDA">DUVIDA - Responder dúvidas do cliente</option>
                    <option value="SUPORTE">SUPORTE - Suporte e atendimento</option>
                    <option value="SAUDACAO">SAUDACAO - Saudações e boas-vindas</option>
                    <option value="FORA_ESCOPO">FORA_ESCOPO - Assuntos fora do escopo</option>
                    <option value="BOBAGEM">BOBAGEM - Conversas casuais</option>
                  </Select>
                  <HelpText>
                    Contexto de uso do script para o assistente
                  </HelpText>
                </FormGroup>

                <FormGroup>
                  <Label $required>Tipo</Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value as 'conteudo' | 'mentalidade')}
                    required
                  >
                    <option value="conteudo">Conteúdo - Script principal</option>
                    <option value="mentalidade">Mentalidade - Comportamento do bot</option>
                  </Select>
                  <HelpText>
                    Tipo técnico do script no sistema
                  </HelpText>
                </FormGroup>
              </FormRow>
            </CardBody>
          </Card>

          <FormFooter>
            <Button type="button" onClick={() => navigate('/neural-vault')}>
              Cancelar
            </Button>
            <Button type="submit" $variant="primary" disabled={!formData.name.trim() || updateScriptMutation.isPending} onClick={handleSubmit}>
              <Save size={16} />
              {updateScriptMutation.isPending ? 'Salvando...' : 'Criar Script'}
            </Button>
          </FormFooter>
        </Form>
      </PageContainer>
    </FadeInContent>
  );
};

export default NewScriptPage;